// /app/frontend/src/utils/unityFlowStorage.js

const STORAGE_KEY = 'unitylove_today_flow_v1';
const BROADCAST_CHANNEL_NAME = 'unity_flow_updates';

// 🎯 BroadcastChannel for cross-tab/cross-component updates (replaces polling)
let broadcastChannel = null;
function getBroadcastChannel() {
  if (typeof window === 'undefined' || !window.BroadcastChannel) return null;
  if (!broadcastChannel) {
    try {
      broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    } catch (e) {
      console.warn('[UnityFlow] BroadcastChannel not available:', e);
    }
  }
  return broadcastChannel;
}

// Broadcast an update to all listeners
export function broadcastFlowUpdate(stepKey) {
  const channel = getBroadcastChannel();
  if (channel) {
    channel.postMessage({ type: 'UNITY_FLOW_UPDATED', step: stepKey, timestamp: Date.now() });
  }
  // Also dispatch a custom event for same-tab listeners
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('unityFlowUpdated', { detail: { step: stepKey } }));
  }
}

// Subscribe to flow updates (for TodayFlow component)
export function subscribeToFlowUpdates(callback) {
  if (typeof window === 'undefined') return () => {};
  
  // Create a dedicated channel for this subscriber (so we can close it on cleanup)
  let subscriberChannel = null;
  if (typeof window !== 'undefined' && window.BroadcastChannel) {
    try {
      subscriberChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    } catch (e) {
      console.warn('[UnityFlow] BroadcastChannel not available for subscriber:', e);
    }
  }
  
  // BroadcastChannel listener (cross-tab, modern browsers)
  const channelHandler = (event) => {
    if (event.data?.type === 'UNITY_FLOW_UPDATED') {
      callback(event.data);
    }
  };
  
  // Custom event listener (same-tab)
  const eventHandler = (event) => {
    callback(event.detail);
  };
  
  // 🎯 Storage event fallback (cross-tab for Safari/older browsers without BroadcastChannel)
  // Only fires when localStorage changes in ANOTHER tab
  const storageHandler = (event) => {
    if (event.key !== STORAGE_KEY) return;
    if (!event.newValue) return;
    callback({ type: 'UNITY_FLOW_UPDATED', step: 'storage_sync', timestamp: Date.now() });
  };
  
  if (subscriberChannel) {
    subscriberChannel.addEventListener('message', channelHandler);
  } else {
    // No BroadcastChannel support - use storage event as cross-tab fallback
    window.addEventListener('storage', storageHandler);
  }
  window.addEventListener('unityFlowUpdated', eventHandler);
  
  // Return unsubscribe function (cleans up ALL listeners + closes channel)
  return () => {
    if (subscriberChannel) {
      subscriberChannel.removeEventListener('message', channelHandler);
      subscriberChannel.close(); // Close this subscriber's channel
    } else {
      window.removeEventListener('storage', storageHandler);
    }
    window.removeEventListener('unityFlowUpdated', eventHandler);
  };
}

// Get today's date as YYYY-MM-DD
function getTodayString() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

// Default empty state
const DEFAULT_STATE = {
  checkedLikes: false,
  sentFlirt: false,
  joinedLounge: false,
};

// Read from localStorage, reset if date changed or invalid
export function getTodayFlowState() {
  if (typeof window === 'undefined') {
    return {
      date: getTodayString(),
      state: { ...DEFAULT_STATE },
    };
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {
        date: getTodayString(),
        state: { ...DEFAULT_STATE },
      };
    }

    const parsed = JSON.parse(raw);
    const today = getTodayString();

    if (!parsed.date || parsed.date !== today || !parsed.state) {
      // New day or bad data → reset
      return {
        date: today,
        state: { ...DEFAULT_STATE },
      };
    }

    return {
      date: parsed.date,
      state: {
        ...DEFAULT_STATE,
        ...parsed.state,
      },
    };
  } catch (e) {
    console.error('[TodayFlow] Failed to read storage', e);
    return {
      date: getTodayString(),
      state: { ...DEFAULT_STATE },
    };
  }
}

// Save state back to localStorage
export function saveTodayFlowState(state) {
  if (typeof window === 'undefined') return;

  try {
    const payload = {
      date: getTodayString(),
      state: {
        ...DEFAULT_STATE,
        ...state,
      },
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (e) {
    console.error('[TodayFlow] Failed to save storage', e);
  }
}

// Map frontend step keys to backend task names
const STEP_TO_TASK = {
  checkedLikes: 'LIKES',
  sentFlirt: 'FLIRT_SENT',
  joinedLounge: 'LOUNGE_VISIT',
};

// Mark a specific step as done (used by pages: who liked me, discover, lounge)
// Now calls backend /api/unity-flow/task for each step
export async function markFlowStep(stepKey) {
  if (typeof window === 'undefined') return;

  try {
    const current = getTodayFlowState();
    
    // Don't re-mark if already done today
    if (current.state[stepKey]) {
      console.log(`[UnityFlow] Step ${stepKey} already done today, skipping`);
      return;
    }
    
    const nextState = {
      ...current.state,
      [stepKey]: true,
    };
    saveTodayFlowState(nextState);
    
    // 🎯 Broadcast update immediately (replaces polling)
    broadcastFlowUpdate(stepKey);

    // 🔥 Call backend to register this task
    const taskName = STEP_TO_TASK[stepKey];
    if (taskName) {
      try {
        const BACKEND_URL = window.location.origin;
        const token = localStorage.getItem('token');
        
        if (token) {
          console.log(`[UnityFlow] Registering task: ${taskName}`);
          const response = await fetch(`${BACKEND_URL}/api/unity-flow/task`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ task: taskName })
          });
          
          if (response.ok) {
            const data = await response.json();
            console.log(`✅ [UnityFlow] Task ${taskName} registered:`, {
              go_count: data.go_count,
              completed_today: data.completed_today,
              streak_current: data.streak_current,
              streak_awarded: data.streak_awarded
            });
            
            // 🎯 FIX: Always refresh Unity Flow UI after any task completion
            // This ensures the green dot updates immediately, not just on focus
            window.refreshUnityFlow?.();
            
            // If streak was just awarded (hit 3/3), refresh global state
            if (data.streak_awarded || data.completed_today) {
              console.log('🔥 [UnityFlow] All 3 tasks complete! Refreshing...');
              window.refreshStreakAndBadges?.();
            }
          } else {
            console.warn(`⚠️ [UnityFlow] Failed to register task ${taskName}:`, response.status);
          }
        }
      } catch (err) {
        console.error(`❌ [UnityFlow] Failed to call unity-flow/task:`, err);
        // Don't throw - localStorage update already succeeded
      }
    }
  } catch (e) {
    console.error('[TodayFlow] Failed to mark step', stepKey, e);
  }
}
